import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Calendar as CalendarIcon, LayoutGrid, MapPin, Clock } from 'lucide-react';
import { useAppointments } from '@/hooks/use-appointments';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import GlassCard from '@/components/ui/glass-card';
import { 
  AppointmentDetailView,
  AppointmentFilters,
  AppointmentCard
} from '@/components/appointments';
// Import modal components directly to avoid barrel file issues
import { RescheduleAppointmentModal } from '@/components/appointments/RescheduleAppointmentModal';
import { CancelAppointmentModal } from '@/components/appointments/CancelAppointmentModal';
import { Toggle } from '@/components/ui/toggle';
import { Badge } from '@/components/ui/badge';
import { Appointment, AppointmentStatus } from '@/types/appointment-types';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import PageContainer from '@/components/layout/PageContainer';

// Define DateRange type to match what AppointmentFilters expects
type DateRange = {
  from: Date;
  to: Date;
} | null;

type ViewMode = 'grid' | 'calendar';

const Appointments: React.FC = () => {
  const navigate = useNavigate();
  const { appointments, isLoading, error } = useAppointments();
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [filters, setFilters] = useState({
    status: '',
    dateRange: null as DateRange,
    searchQuery: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filteredAppointments, setFilteredAppointments] = useState<Appointment[]>([]);
  
  // Add selectedAppointment state back
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  
  // Separate state declarations to avoid redeclaration issues
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  useEffect(() => {
    if (appointments) {
      let result = [...appointments];
      
      // Apply filters
      if (filters.status) {
        result = result.filter(
          (apt) => apt.status.toLowerCase() === filters.status.toLowerCase()
        );
      }
      
      if (filters.dateRange) {
        const { from, to } = filters.dateRange;
        if (from && to) {
          result = result.filter((apt) => {
            const aptDate = new Date(apt.startTime);
            return aptDate >= from && aptDate <= to;
          });
        }
      }
      
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        result = result.filter(
          (apt) =>
            apt.appointmentType?.toLowerCase().includes(query) ||
            apt.facilityId?.toLowerCase().includes(query) ||
            apt.reason?.toLowerCase().includes(query)
        );
      }
      
      setFilteredAppointments(result);
    }
  }, [appointments, filters]);

  // Functions to show modals
  const showDetailModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsDetailModalOpen(true);
  };

  const showRescheduleModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsRescheduleModalOpen(true);
  };

  const showCancelModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsCancelModalOpen(true);
  };

  // Function to get status badge
  const getStatusBadge = (status: AppointmentStatus) => {
    let className = '';
    
    switch (status) {
      case 'scheduled':
        className = 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
        break;
      case 'confirmed':
        className = 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
        break;
      case 'completed':
        className = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
        break;
      case 'cancelled':
        className = 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
        break;
      case 'rescheduled':
        className = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
        break;
      default:
        className = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
    
    return (
      <Badge variant="outline" className={className}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  // Get appointment type colors for consistent event styling
  const getAppointmentTypeStyles = (appointmentType: string | null | undefined, status: AppointmentStatus) => {
    // Default colors if no match is found
    let backgroundColor, borderColor, textColor;
    
    // First determine by status for cancelled/completed
    if (status === 'cancelled') {
      return {
        backgroundColor: 'rgba(239, 68, 68, 0.15)',
        borderColor: 'rgba(239, 68, 68, 0.6)',
        textColor: 'rgb(185, 28, 28)'
      };
    } else if (status === 'completed') {
      return {
        backgroundColor: 'rgba(107, 114, 128, 0.15)',
        borderColor: 'rgba(107, 114, 128, 0.6)',
        textColor: 'rgb(55, 65, 81)'
      };
    }
    
    // Then determine by appointment type
    const type = (appointmentType || '').toLowerCase();
    
    if (type.includes('test') || type.includes('lab') || type.includes('blood')) {
      backgroundColor = 'rgba(139, 92, 246, 0.15)';
      borderColor = 'rgba(139, 92, 246, 0.6)';
      textColor = 'rgb(91, 33, 182)';
    } else if (type.includes('physical') || type.includes('checkup') || type.includes('wellness')) {
      backgroundColor = 'rgba(34, 211, 238, 0.15)';
      borderColor = 'rgba(34, 211, 238, 0.6)';
      textColor = 'rgb(8, 145, 178)';
    } else if (type.includes('surgery') || type.includes('procedure')) {
      backgroundColor = 'rgba(239, 68, 68, 0.15)';
      borderColor = 'rgba(239, 68, 68, 0.6)';
      textColor = 'rgb(153, 27, 27)';
    } else if (type.includes('therapy') || type.includes('mental') || type.includes('counseling')) {
      backgroundColor = 'rgba(16, 185, 129, 0.15)';
      borderColor = 'rgba(16, 185, 129, 0.6)';
      textColor = 'rgb(6, 95, 70)';
    } else if (type.includes('dental') || type.includes('teeth')) {
      backgroundColor = 'rgba(245, 158, 11, 0.15)';
      borderColor = 'rgba(245, 158, 11, 0.6)';
      textColor = 'rgb(146, 64, 14)';
    } else if (type.includes('consult') || type.includes('specialist')) {
      backgroundColor = 'rgba(59, 130, 246, 0.15)';
      borderColor = 'rgba(59, 130, 246, 0.6)';
      textColor = 'rgb(29, 78, 216)';
    } else if (type.includes('vaccine') || type.includes('immunization') || type.includes('shot')) {
      backgroundColor = 'rgba(168, 85, 247, 0.15)';
      borderColor = 'rgba(168, 85, 247, 0.6)';
      textColor = 'rgb(107, 33, 168)';
    } else if (type.includes('imaging') || type.includes('x-ray') || type.includes('mri') || type.includes('scan')) {
      backgroundColor = 'rgba(251, 113, 133, 0.15)';
      borderColor = 'rgba(251, 113, 133, 0.6)';
      textColor = 'rgb(190, 18, 60)';
    } else {
      // Default for general appointments
      switch (status) {
        case 'scheduled':
          backgroundColor = 'rgba(59, 130, 246, 0.15)';
          borderColor = 'rgba(59, 130, 246, 0.6)';
          textColor = 'rgb(29, 78, 216)';
          break;
        case 'confirmed':
          backgroundColor = 'rgba(16, 185, 129, 0.15)';
          borderColor = 'rgba(16, 185, 129, 0.6)';
          textColor = 'rgb(4, 120, 87)';
          break;
        case 'rescheduled':
          backgroundColor = 'rgba(245, 158, 11, 0.15)';
          borderColor = 'rgba(245, 158, 11, 0.6)';
          textColor = 'rgb(146, 64, 14)';
          break;
        default:
          backgroundColor = 'rgba(99, 102, 241, 0.15)';
          borderColor = 'rgba(99, 102, 241, 0.6)';
          textColor = 'rgb(67, 56, 202)';
      }
    }
    
    return { backgroundColor, borderColor, textColor };
  };

  // Map appointments to FullCalendar event format
  const calendarEvents = useMemo(() => {
    return filteredAppointments.map(apt => {
      // Get styling based on appointment type and status
      const { backgroundColor, borderColor, textColor } = getAppointmentTypeStyles(apt.appointmentType, apt.status);
      
      // Add location to title if available
      const facilityName = apt.facilityId || 'Main Medical Center';
      const titleWithLocation = `${apt.appointmentType || 'Appointment'}`;
      
      return {
        id: apt.id,
        title: titleWithLocation,
        start: apt.startTime,
        end: apt.endTime,
        allDay: false,
        extendedProps: { 
          appointment: apt,
          provider: apt.providerName,
          location: facilityName,
          status: apt.status
        },
        // Custom styling for pill-shaped events with status colors
        backgroundColor,
        borderColor,
        textColor,
        borderWidth: '1px',
        classNames: `appointment-pill appointment-${apt.status}`
      };
    });
  }, [filteredAppointments]);

  // Add custom CSS for FullCalendar events to make them pill-shaped
  useEffect(() => {
    // Add custom CSS for calendar events
    const style = document.createElement('style');
    style.innerHTML = `
      /* Calendar container styling */
      .fc {
        --fc-border-color: var(--border-subtle, rgba(229, 231, 235, 0.6));
        --fc-today-bg-color: var(--brand-primary-subtle, rgba(59, 130, 246, 0.08));
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      }
      
      /* Header styling */
      .fc .fc-toolbar.fc-header-toolbar {
        margin-bottom: 1.25em;
        flex-wrap: wrap;
        gap: 0.75rem;
        padding: 0 0.5rem;
      }
      
      .fc .fc-toolbar-title {
        font-size: 1.4rem;
        font-weight: 700;
        color: var(--text-primary);
        letter-spacing: -0.01em;
      }
      
      /* Calendar grid styling */
      .fc-theme-standard .fc-scrollgrid {
        border-radius: 12px;
        overflow: hidden;
        border-width: 1px;
        border-color: var(--border-subtle);
        box-shadow: 0 2px 8px rgba(0,0,0,0.03);
      }
      
      .fc-theme-standard td, .fc-theme-standard th {
        border-color: var(--border-subtle, rgba(229, 231, 235, 0.6));
      }
      
      /* Header cells */
      .fc-col-header-cell {
        background-color: var(--background-subtle);
        padding: 12px 0;
      }
      
      .fc .fc-col-header-cell-cushion {
        font-weight: 600 !important;
        font-size: 0.8rem;
        padding: 6px;
        color: var(--text-primary);
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
      
      /* Day cells */
      .fc .fc-daygrid-day {
        padding: 2px;
        transition: background-color 0.15s ease;
      }
      
      .fc .fc-daygrid-day:hover {
        background-color: rgba(0, 0, 0, 0.02);
      }
      
      .fc .fc-daygrid-day-top {
        justify-content: center;
        padding-top: 4px;
      }
      
      .fc .fc-daygrid-day-number {
        font-size: 0.85rem;
        font-weight: 500;
        color: var(--text-secondary);
        width: 100%;
        text-align: center;
        padding: 2px 0;
      }
      
      /* Today styling */
      .fc .fc-daygrid-day.fc-day-today {
        background-color: var(--brand-primary-subtle, rgba(59, 130, 246, 0.08)) !important;
      }
      
      .fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-number {
        background-color: var(--brand-primary);
        color: white;
        font-weight: 600;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }
      
      /* Event styling - Medical themed */
      .fc-event {
        border-radius: 8px !important;
        padding: 2px 8px !important;
        margin: 1px !important;
        border-left-width: 3px !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
        overflow: hidden !important;
        font-size: 0.75rem !important;
        line-height: 1.4 !important;
        transition: all 0.2s ease-in-out !important;
        position: relative !important;
      }
      
      /* Compact events for days with multiple events */
      .fc-dayGridMonth-view .fc-event {
        height: auto !important;
        min-height: 22px !important;
        display: flex !important;
        align-items: center !important;
      }
      
      /* Time indicator */
      .fc-event-time {
        font-weight: 600 !important;
        margin-right: 4px !important;
        font-size: 0.7rem !important;
        opacity: 0.9 !important;
      }
      
      /* Event title */
      .fc-event-title {
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        font-weight: 500 !important;
      }
      
      /* Status-specific gradient styling */
      .appointment-scheduled {
        background: linear-gradient(to right, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.05)) !important;
        border-left-color: rgba(59, 130, 246, 0.8) !important;
      }
      
      .appointment-confirmed {
        background: linear-gradient(to right, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.05)) !important;
        border-left-color: rgba(16, 185, 129, 0.8) !important;
      }
      
      .appointment-rescheduled {
        background: linear-gradient(to right, rgba(245, 158, 11, 0.15), rgba(245, 158, 11, 0.05)) !important;
        border-left-color: rgba(245, 158, 11, 0.8) !important;
      }
      
      .appointment-cancelled {
        background: linear-gradient(to right, rgba(239, 68, 68, 0.15), rgba(239, 68, 68, 0.05)) !important;
        border-left-color: rgba(239, 68, 68, 0.8) !important;
        text-decoration: line-through !important;
        opacity: 0.85 !important;
      }
      
      .appointment-completed {
        background: linear-gradient(to right, rgba(107, 114, 128, 0.15), rgba(107, 114, 128, 0.05)) !important;
        border-left-color: rgba(107, 114, 128, 0.8) !important;
        opacity: 0.9 !important;
      }
      
      /* Event hover effects */
      .fc-event:hover {
        transform: translateY(-1px) !important;
        box-shadow: 0 3px 6px rgba(0, 0, 0, 0.08) !important;
      }
      
      /* Button styling */
      .fc-button-primary {
        background-color: var(--background-subtle) !important;
        border-color: var(--border-subtle) !important;
        color: var(--text-primary) !important;
        font-weight: 500 !important;
        font-size: 0.875rem !important;
        padding: 0.35rem 0.7rem !important;
        border-radius: 0.375rem !important;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06) !important;
        transition: all 0.2s ease !important;
      }
      
      .fc-button-primary:hover {
        background-color: var(--background-interactive-hover) !important;
        color: var(--brand-primary) !important;
      }
      
      .fc-button-primary.fc-button-active {
        background-color: var(--brand-primary) !important;
        border-color: var(--brand-primary) !important;
        color: white !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
      }
      
      /* Responsive adjustments */
      @media (max-width: 640px) {
        .fc .fc-toolbar {
          flex-direction: column;
          align-items: start;
        }
        
        .fc .fc-toolbar-chunk {
          margin-bottom: 0.5rem;
        }
        
        .fc-event {
          padding: 1px 4px !important;
          font-size: 0.65rem !important;
        }
      }

      /* Weekend days styling */
      .fc-day-sat, .fc-day-sun {
        background-color: rgba(0, 0, 0, 0.01);
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Handle clicking on a calendar event
  const handleEventClick = (clickInfo: any) => {
    const appointment = clickInfo.event.extendedProps.appointment as Appointment;
    if (appointment) {
      showDetailModal(appointment);
    }
  };

  // Handle clicking on a date (optional: for booking)
  const handleDateClick = (_arg: any) => {
    // You might want to implement logic here to set selectedDateRange 
    // and open the booking modal
  };

  // Create view mode toggles as a component to place in header
  const ViewModeToggles = () => (
    <div className="flex items-center space-x-2 ml-2">
      <Toggle
        pressed={viewMode === 'grid'}
        onPressedChange={() => setViewMode('grid')}
        aria-label="Grid view"
        size="sm"
        className="h-8 px-3 text-sm font-medium"
      >
        <LayoutGrid className="h-4 w-4 mr-1.5" />
        Grid
      </Toggle>
      <Toggle
        pressed={viewMode === 'calendar'}
        onPressedChange={() => setViewMode('calendar')}
        aria-label="Calendar view"
        size="sm"
        className="h-8 px-3 text-sm font-medium"
      >
        <CalendarIcon className="h-4 w-4 mr-1.5" />
        Calendar
      </Toggle>
    </div>
  );

  return (
    <PageContainer
      title="Appointments"
      description="View and manage your upcoming and past appointments"
      icon={<CalendarIcon className="h-5 w-5 text-indigo-500" />}
      actions={
        <div className="flex items-center gap-2">
          <ViewModeToggles />
          <Button 
            variant="outline" 
            size="sm"
            className="border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2 text-indigo-500" />
            Filters
          </Button>
          <Button 
            onClick={() => navigate('/schedule-appointment')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Book Appointment
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Search and filter section with refined styling */}
        <GlassCard padding="sm" variant="default" className="flex flex-col sm:flex-row items-start gap-4 justify-between w-full shadow-sm border-white/40">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <Input
              type="search"
              placeholder="Search appointments..."
              className="pl-10 border-white/20 dark:border-white/10 w-full bg-white/50 dark:bg-slate-800/50 shadow-sm focus-visible:ring-1 focus-visible:ring-[var(--brand-primary)]"
              value={filters.searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                setFilters({ ...filters, searchQuery: e.target.value })
              }
            />
          </div>
          {showFilters ? (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowFilters(false)}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
            >
              Hide Filters
            </Button>
          ) : null}
        </GlassCard>

        {/* Filters */}
        {showFilters && (
          <GlassCard padding="md" variant="subtle" className="shadow-inner border-white/20">
            <AppointmentFilters 
              filters={filters}
              setFilters={setFilters}
            />
          </GlassCard>
        )}

        {/* Content based on view mode */}
        {isLoading ? (
          <div className="flex justify-center items-center py-10">
            <p>Loading appointments...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-900/30 dark:text-red-400 px-5 py-4 rounded-lg shadow-sm">
            Error loading appointments. Please try again.
          </div>
        ) : (
          <>
            {/* Grid View */}
            {viewMode === 'grid' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredAppointments.length > 0 ? (
                  filteredAppointments.map((appointment) => (
                    <AppointmentCard
                      key={appointment.id}
                      appointment={appointment}
                      onClick={showDetailModal}
                      onReschedule={showRescheduleModal}
                      onCancel={showCancelModal}
                      statusBadge={getStatusBadge(appointment.status)}
                      isGrid={true}
                    />
                  ))
                ) : (
                  <GlassCard padding="lg" variant="subtle" className="col-span-full py-16 flex flex-col items-center justify-center text-center shadow-inner border-white/20">
                    <CalendarIcon className="h-12 w-12 text-slate-300 dark:text-slate-600 mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 dark:text-slate-200 mb-1">No appointments found</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 max-w-md">
                      {filters.searchQuery || filters.status || filters.dateRange 
                        ? "Try adjusting your filters to see more results." 
                        : "You don't have any appointments scheduled."}
                    </p>
                    <Button 
                      onClick={() => navigate('/schedule-appointment')}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Book Appointment
                    </Button>
                  </GlassCard>
                )}
              </div>
            )}

            {/* Calendar View */}
            {viewMode === 'calendar' && (
              <GlassCard padding="none" variant="default" className="mt-6 shadow-sm overflow-hidden border-white/40">
                <FullCalendar
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay'
                  }}
                  events={calendarEvents}
                  eventClick={handleEventClick}
                  dateClick={handleDateClick}
                  editable={false}
                  selectable={true}
                  height="auto"
                  aspectRatio={1.8}
                  eventTimeFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    meridiem: 'short'
                  }}
                  eventContent={(arg) => {
                    const location = arg.event.extendedProps.location;
                    
                    // For month view, simplify display to avoid overflow
                    if (arg.view.type === 'dayGridMonth') {
                      return (
                        <div className="fc-event-main-content overflow-hidden whitespace-nowrap flex items-center w-full">
                          <span className="fc-event-title truncate">{arg.event.title}</span>
                        </div>
                      );
                    }
                    
                    // For week/day view, show more details
                    return (
                      <div className="fc-event-main-content">
                        {arg.timeText && (
                          <div className="fc-event-time flex items-center">
                            <Clock className="h-3 w-3 inline mr-1" />
                            <span>{arg.timeText}</span>
                          </div>
                        )}
                        <span className="fc-event-title font-medium">{arg.event.title}</span>
                        {location && (
                          <div className="fc-event-location text-xs mt-0.5 opacity-80 flex items-center">
                            <MapPin className="h-3 w-3 inline mr-1" />
                            <span>{location}</span>
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
              </GlassCard>
            )}
          </>
        )}

        {/* Appointment Detail Modal */}
        {selectedAppointment && isDetailModalOpen && (
          <AppointmentDetailView
            appointment={selectedAppointment}
            isOpen={isDetailModalOpen}
            onClose={() => setIsDetailModalOpen(false)}
            onReschedule={showRescheduleModal}
            onCancel={(id) => {
              const appToCancel = appointments.find(a => a.id === id);
              if (appToCancel) showCancelModal(appToCancel);
            }}
          />
        )}

        {/* Reschedule Modal */}
        {selectedAppointment && isRescheduleModalOpen && (
          <RescheduleAppointmentModal
            appointment={selectedAppointment}
            isOpen={isRescheduleModalOpen}
            onClose={() => setIsRescheduleModalOpen(false)}
          />
        )}

        {/* Cancel Modal - Pass the full appointment object and isOpen */}
        {selectedAppointment && isCancelModalOpen && (
          <CancelAppointmentModal
            appointment={selectedAppointment} 
            isOpen={isCancelModalOpen}
            onClose={() => setIsCancelModalOpen(false)}
          />
        )}
      </div>
    </PageContainer>
  );
};

export default Appointments; 